/* eslint-disable jsx-a11y/media-has-caption */
import {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Box, Grid } from '@mui/material';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import BlockVideoData from './BlockVideoData';
import VideoSettingsModal from '../../Modals/VideoSettingsModal/VideoSettingsModal';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';

function BlockVideo({
  data,
  isEditing,
  isTemplate,
  editHandler,
}: BlockProps) {
  const blockId = data.id;
  const props = (data.data as BlockVideoData);

  const videoRef = createRef<HTMLVideoElement>();
  const [showModal, setShowModal] = useState(false);
  const [videoSrc, setVideoSrc] = useState(props.video.src);
  const [minVideoHeight, setMinVideoHeight] = useState(250);
  const textRef = useRef(null);

  useEffect(() => {
    const updateVideoHeight = () => {
      if (textRef.current) {
        const textHeight = textRef.current.offsetHeight + 24;
        setMinVideoHeight(Math.max(250, textHeight));
      }
    };

    const observer = new ResizeObserver(updateVideoHeight);
    if (textRef.current) observer.observe(textRef.current);

    window.addEventListener('resize', updateVideoHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateVideoHeight);
    };
  }, []);

  const reloadVideo = useCallback(() => {
    if (videoRef.current) videoRef.current.load();
  }, [videoRef]);

  const verifyIfTitleExists = (title: string) => {
    for (let i = 0; i < 6; i++) {
      if (title === `<h${i}><br></h${i}>`) return false;
    }

    if (title === '<p><br></p>') return false;

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          backgroundPosition: 'center 0px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Grid
          container
          spacing={0}
          direction="row"
          alignItems="stretch"
          justifyContent="space-between"
          sx={{
            minHeight: '250px !important',
            width: '70%',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '250px',
            }}
          >
            {isEditing && !isTemplate && (
              <VideoSettingsModal
                title={props.title}
                video={props.video}
                showModal={showModal}
                setShowModal={setShowModal}
                onConfirm={(
                  title,
                  videoSrc,
                  backgroundColor,
                  loop,
                  autoPlay,
                  muted,
                  staticBackground,
                ) => {
                  props.title.value = title;
                  props.video.src = videoSrc;
                  props.title.settings.backgroundColor = backgroundColor;
                  props.video.settings.loop = loop;
                  props.video.settings.autoplay = autoPlay;
                  props.video.settings.muted = muted;
                  props.video.settings.staticBackground = staticBackground;

                  setVideoSrc(videoSrc);
                  editHandler(blockId, props);
                  reloadVideo();
                }}
              />
            )}

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: '250px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {props.title && (
                <Box
                  ref={textRef}
                  sx={{
                    position: 'absolute',
                    textAlign: 'center',
                    background: props.title.settings?.backgroundColor,
                    padding: '12px 16px',
                    zIndex: 2,
                    maxWidth: '90%',
                  }}
                >
                  <Box sx={Utils.hoverBorder(isEditing)}>

                    {isEditing && (
                      <CloseButton updateHandler={() => {
                        props.title = undefined;
                        editHandler(data.id, props);
                      }}
                      />
                    )}

                    <EditableText
                      text={props.title.value}
                      isEditable={isEditing && !isTemplate}
                      updateHandler={() => { }}
                    />
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  width: '100%',
                  minHeight: `${minVideoHeight}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <video
                  ref={videoRef}
                  loop={!!props.video.settings?.loop}
                  autoPlay={!!props.video.settings?.autoplay}
                  muted={!!props.video.settings?.muted}
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    minHeight: `${minVideoHeight}px`,
                  }}
                  onClick={() => { if (isEditing) setShowModal(true); }}
                >
                  <source src={videoSrc} type="video/mp4" />
                </video>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default BlockVideo;
